import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Grid,
  Header,
  Link,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { getVoiceSalesCallDetail, getVoiceSalesRecordingMedia } from '../../common/api/voiceSales';
import type { VoiceSalesCallDetail, VoiceSalesRecording } from '../../common/api/voiceSales';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { PhoneNumber } from '../../components/PhoneNumber';
import { formatDuration, formatStatusLabel, VoiceSalesStatus } from './presentation';
import { RichContext } from './RichContext';

function conversationRole(role: string): string {
  if (role === 'assistant') return 'Voice assistant';
  if (role === 'user') return 'Contact';
  if (role === 'provider' || role === 'system') return 'Call activity';
  return formatStatusLabel(role);
}

type VoiceSalesContext = NonNullable<VoiceSalesCallDetail['context']>;
type VoiceSalesEvent = VoiceSalesCallDetail['events'][number];

function ContextValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

function contactRole(context: VoiceSalesContext | null | undefined): string | null {
  const values = [context?.contact?.title, context?.contact?.department].filter(
    (value): value is string => Boolean(value),
  );
  return values.length ? values.join(' · ') : null;
}

function RecordingPlayback({ recording }: { recording: VoiceSalesRecording }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const mediaQuery = useQuery({
    queryKey: ['voice-sales-recording-media', recording.uuid, recording.media_state],
    queryFn: () => getVoiceSalesRecordingMedia(recording),
    enabled: recording.media_state === 'ready' && Boolean(recording.media_url),
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!mediaQuery.data) {
      setObjectUrl(null);
      return;
    }
    const nextObjectUrl = URL.createObjectURL(mediaQuery.data);
    setObjectUrl(nextObjectUrl);
    return () => URL.revokeObjectURL(nextObjectUrl);
  }, [mediaQuery.data]);

  if (recording.media_state === 'processing') {
    return (
      <Alert statusIconAriaLabel="Processing" header="Recording processing">
        Audio will appear when secure processing is complete.
      </Alert>
    );
  }
  if (recording.media_state !== 'ready' || !recording.media_url) {
    return (
      <Alert statusIconAriaLabel="Unavailable" header="Playback unavailable">
        Secure audio is not available for this recording.
      </Alert>
    );
  }
  if (mediaQuery.isError) {
    return (
      <Alert
        type="error"
        statusIconAriaLabel="Error"
        header="Playback unavailable"
        action={<Button onClick={() => void mediaQuery.refetch()}>Retry playback</Button>}
      >
        The secure recording could not be loaded.
      </Alert>
    );
  }
  if (mediaQuery.isPending || !objectUrl) {
    return (
      <Box color="text-body-secondary">
        <Spinner /> Loading secure recording
      </Box>
    );
  }
  return (
    <audio
      aria-label="Call recording"
      controls
      preload="metadata"
      src={objectUrl}
      style={{ width: '100%', minHeight: '54px' }}
    />
  );
}

function ConversationTimeline({ events }: { events: VoiceSalesEvent[] }) {
  const items = useMemo(
    () =>
      events
        .filter((event) => event.transcript || event.action)
        .slice()
        .sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()),
    [events],
  );

  if (items.length === 0) {
    return <Box textAlign="center">No transcript is available for this call.</Box>;
  }

  return (
    <ol
      aria-label="Conversation timeline"
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {items.map((item) => (
        <li
          key={item.uuid}
          role="listitem"
          data-voice-sales-turn
          style={{
            borderInlineStart: '3px solid #0972d3',
            marginBlockEnd: '1rem',
            paddingInlineStart: '1rem',
            overflowWrap: 'anywhere',
          }}
        >
          <SpaceBetween size="xxs">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Box fontWeight="bold">{conversationRole(item.actor ?? 'system')}</Box>
              <Box variant="small" color="text-body-secondary">
                {formatDateStringWithTime(item.created_at)}
              </Box>
            </SpaceBetween>
            {item.transcript ? <Box variant="p">{item.transcript}</Box> : null}
            {item.action ? (
              <StatusIndicator type={item.outcome === 'failed' ? 'error' : 'info'}>
                {formatStatusLabel(item.action)}
              </StatusIndicator>
            ) : null}
          </SpaceBetween>
        </li>
      ))}
    </ol>
  );
}

function RecordingAndFollowUp({ detail }: { detail: VoiceSalesCallDetail }) {
  return (
    <SpaceBetween size="l">
      <SpaceBetween size="m">
        <Header variant="h3">Recording</Header>
        {detail.recordings.length === 0 ? (
          <Alert type="info" header="Recording unavailable">
            No recording was captured for this call.
          </Alert>
        ) : (
          detail.recordings.map((recording, index) => (
            <div key={recording.uuid} style={{ borderBlockStart: index ? '1px solid #e9ebed' : undefined, paddingBlockStart: index ? '1rem' : undefined }}>
              <SpaceBetween size="s">
                <Header variant="h3">Recording {index + 1}</Header>
                <ColumnLayout columns={2} variant="text-grid">
                  <ContextValue label="Status"><VoiceSalesStatus status={recording.status} /></ContextValue>
                  <ContextValue label="Duration">{formatDuration(recording.duration_seconds)}</ContextValue>
                  <ContextValue label="Jurisdiction">{recording.jurisdiction_label ?? 'Jurisdiction unavailable'}</ContextValue>
                  <ContextValue label="Consent basis">
                    {recording.consent_basis ? formatStatusLabel(recording.consent_basis) : 'Consent basis unavailable'}
                  </ContextValue>
                </ColumnLayout>
                <RecordingPlayback recording={recording} />
              </SpaceBetween>
            </div>
          ))
        )}
      </SpaceBetween>

      <SpaceBetween size="m">
        <Header variant="h3">Follow-up activity</Header>
        {detail.scheduler_link_sends.length === 0 ? (
          <Box color="text-body-secondary">No follow-up activity was recorded.</Box>
        ) : (
          detail.scheduler_link_sends.map((item) => (
            <div key={item.uuid} style={{ borderBlockStart: '1px solid #e9ebed', paddingBlockStart: '0.75rem', overflowWrap: 'anywhere' }}>
              <SpaceBetween size="xxs">
                <Box fontWeight="bold">Scheduler follow-up</Box>
                <VoiceSalesStatus status={item.status} />
                <Link href={item.link_url} external>{item.link_url}</Link>
                <Box variant="p">{item.message_body}</Box>
              </SpaceBetween>
            </div>
          ))
        )}
      </SpaceBetween>
    </SpaceBetween>
  );
}

function errorStatus(error: unknown): number {
  return typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status?: unknown }).status ?? 0)
    : 0;
}

export default function VoiceSalesCallDetailPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { callUuid?: string; uuid?: string };
  const callUuid = params.callUuid ?? params.uuid ?? '';
  const detailQuery = useQuery({
    queryKey: ['voice-sales-call-detail', callUuid],
    queryFn: () => getVoiceSalesCallDetail(callUuid),
    enabled: Boolean(callUuid),
    retry: false,
    refetchInterval: (query) => (query.state.status === 'error' ? false : 15_000),
  });
  const detail = detailQuery.data;
  const call = detail?.call;
  const context = detail?.context;
  const contact = context?.contact;
  const contactName = contact?.name ?? 'Call details';
  const role = contactRole(context);
  const status = errorStatus(detailQuery.error);

  return (
    <DashboardViewLayout
      headerVariant="high-contrast"
      breadcrumbs={[
        { text: 'Voice Sales', href: '/dashboard/voice-sales' },
        { text: 'Calls', href: '/dashboard/voice-sales/calls' },
        { text: 'Call details', href: callUuid ? `/dashboard/voice-sales/calls/${callUuid}` : '' },
      ]}
      header={
        <Container
          header={
            <Header
              variant="h1"
              description={
                <span>
                  {role ? (
                    <>
                      <strong>{role}</strong>
                      <br />
                    </>
                  ) : null}
                  Review the outcome, conversation, recording availability, and approved follow-up.
                </span>
              }
              actions={
                <Button iconName="arrow-left" onClick={() => navigate({ to: '/dashboard/voice-sales/calls' })}>
                  Back to calls
                </Button>
              }
            >
              {contactName}
            </Header>
          }
        >
          {call ? (
            <ColumnLayout columns={4} variant="text-grid">
              <ContextValue label="Contact phone">
                <PhoneNumber
                  phoneNumber={call.display_phone_number}
                  lineTypeInfo={call.phone_line_type_info}
                  showLineTypeInfo
                />
              </ContextValue>
              {!contact ? (
                <ContextValue label="Contact">Contact details unavailable</ContextValue>
              ) : null}
              {detail?.campaign?.uuid ? (
                <ContextValue label="Campaign">
                  <Link href={`/dashboard/voice-sales/campaigns/${detail.campaign.uuid}`}>
                    {detail.campaign.name}
                  </Link>
                </ContextValue>
              ) : null}
              <ContextValue label="Status"><VoiceSalesStatus status={call.status} error={call.error} /></ContextValue>
              <ContextValue label="Outcome"><VoiceSalesStatus status={call.outcome} /></ContextValue>
              <ContextValue label="Duration">{formatDuration(call.duration_seconds)}</ContextValue>
              <ContextValue label="Started">
                {call.started_at ? formatDateStringWithTime(call.started_at) : 'Not started'}
              </ContextValue>
              <ContextValue label="Ended">
                {call.completed_at ? formatDateStringWithTime(call.completed_at) : 'Not ended'}
              </ContextValue>
            </ColumnLayout>
          ) : null}
        </Container>
      }
    >
      {detailQuery.isPending ? (
        <StatusIndicator type="loading">Loading call details</StatusIndicator>
      ) : detailQuery.isError ? (
        status === 403 ? (
          <Alert type="error" header="You do not have access to Voice Sales.">
            Ask an internal administrator if you believe you need access.
          </Alert>
        ) : status === 404 ? (
          <Alert
            type="warning"
            header="Call not found"
            action={<Button onClick={() => detailQuery.refetch()}>Retry</Button>}
          >
            This call does not exist or is no longer available.
          </Alert>
        ) : (
          <Alert
            type="error"
            header="Call detail could not be loaded"
            action={<Button onClick={() => detailQuery.refetch()}>Retry</Button>}
          >
            Retry to load the call record.
          </Alert>
        )
      ) : detail && call ? (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Business and scorecard</Header>}>
            {detail.context ? (
              <RichContext
                context={detail.context}
                sections={['business', 'scorecard']}
                layout="columns"
              />
            ) : (
              <Alert type="info" header="Business details unavailable">
                Business and scorecard details are not available for this call yet. Call status and
                conversation evidence remain available below.
              </Alert>
            )}
          </Container>
          <Grid gridDefinition={[{ colspan: { default: 12, s: 8 } }, { colspan: { default: 12, s: 4 } }]}>
            <Container
              header={
                <Header variant="h2" description="Speaker turns and call actions in chronological order.">
                  Conversation timeline
                </Header>
              }
            >
              <ConversationTimeline events={detail.events} />
            </Container>
            <Container header={<Header variant="h2">Recording and follow-up</Header>}>
              <RecordingAndFollowUp detail={detail} />
            </Container>
          </Grid>
        </SpaceBetween>
      ) : (
        <Alert type="error">The call record is unavailable.</Alert>
      )}
    </DashboardViewLayout>
  );
}
